import store from '../../../../state';
import { ForecastWidgetConfig, WidgetRenderContext, WidgetRenderer } from '../types';
import { buildLocalCalendarDayBuckets, resolveSourceBadge } from './DailyForecastListWidget';
import { ForecastBinaryService } from '../../../../services/ForecastBinaryService';
import TierAccessService from '../../../../services/TierAccessService';

export interface DailySnowSummary {
  dayLabel: string;
  dateSub: string;
  icon: string;
  condition: string;
  snowIn: number;
  snowDisplay: string;
  snowProbPct: number;
  peakRateInH: number;
}

export function aggregateDailySnow(ctx: WidgetRenderContext, overrideDays?: number | string): DailySnowSummary[] {
  const data = ctx.data;
  if (!data || !data.tempsForecast || data.tempsForecast.length === 0) {
    return [];
  }

  const isImperial = store.getState().units === 'imperial';
  const temps = data.tempsForecast;
  const precip = data.precipLikelihood || [];
  const snowRate = data.snowRate || [];
  const rainRate = data.precipRate || [];

  const buckets = buildLocalCalendarDayBuckets(ctx, overrideDays);
  const summaries: DailySnowSummary[] = [];

  for (const bucket of buckets) {
    if (bucket.hourIndices.length === 0) continue;

    const sliceTemps = bucket.hourIndices.map(i => temps[i] ?? 50);
    const slicePrecip = bucket.hourIndices.map(i => precip[i] ?? 0);
    const sliceSnow = bucket.hourIndices.map(i => snowRate[i] ?? 0);
    const sliceRain = bucket.hourIndices.map(i => rainRate[i] ?? 0);

    const maxPoP = slicePrecip.length ? Math.round(Math.max(...slicePrecip)) : 0;
    let rawDailySnowIn = sliceSnow.reduce((acc, s) => acc + (s > 0 ? s : 0), 0);

    // Dynamic temperature-dependent snowfall fallback if snowRate array is not directly populated
    if (rawDailySnowIn === 0 && sliceRain.length > 0) {
      for (let h = 0; h < sliceRain.length; h++) {
        const t = sliceTemps[h] ?? 50;
        const r = sliceRain[h] ?? 0;
        if (r > 0 && t <= 34) {
          // Standard Kuchera / SLR approximation for freezing temperatures
          const slr = t <= 20 ? 15.0 : (t <= 28 ? 12.0 : 8.0);
          rawDailySnowIn += r * slr;
        }
      }
    }

    const roundedSnowIn = Math.round(rawDailySnowIn * 10) / 10;
    const peakRate = sliceSnow.length ? Math.max(...sliceSnow) : 0;

    let snowDisplay: string;
    if (isImperial) {
      snowDisplay = roundedSnowIn >= 0.1 ? `${roundedSnowIn.toFixed(1)}″` : (maxPoP >= 30 && (sliceTemps.some(t => t <= 34)) ? 'Trace' : '0.0″');
    } else {
      const cm = Math.round(roundedSnowIn * 2.54 * 10) / 10;
      snowDisplay = cm >= 0.2 ? `${cm.toFixed(1)} cm` : (maxPoP >= 30 && (sliceTemps.some(t => t <= 34)) ? 'Trace' : '0.0 cm');
    }

    let icon = '☀️';
    let condition = 'No Snow';
    if (roundedSnowIn >= 6.0 || peakRate >= 1.0) {
      icon = '🌨️';
      condition = 'Heavy Snow';
    } else if (roundedSnowIn >= 2.0 || (maxPoP >= 60 && roundedSnowIn >= 0.5)) {
      icon = '🌨️';
      condition = 'Snow Likely';
    } else if (roundedSnowIn >= 0.5 || (maxPoP >= 40 && sliceTemps.some(t => t <= 34))) {
      icon = '❄️';
      condition = 'Snow Showers';
    } else if (roundedSnowIn > 0 || (maxPoP >= 25 && sliceTemps.some(t => t <= 32))) {
      icon = '❄️';
      condition = 'Flurries / Trace';
    } else {
      icon = '☀️';
      condition = 'Clear / Dry';
    }

    summaries.push({
      dayLabel: bucket.dayLabel,
      dateSub: bucket.dateSub,
      icon,
      condition,
      snowIn: roundedSnowIn,
      snowDisplay,
      snowProbPct: (roundedSnowIn > 0 || sliceTemps.some(t => t <= 34)) ? maxPoP : 0,
      peakRateInH: Math.round(peakRate * 10) / 10
    });
  }

  return summaries;
}

export class DailySnowWidget implements WidgetRenderer {
  public renderHtml(widget: ForecastWidgetConfig, ctx: WidgetRenderContext): string {
    const title = widget.title || 'Daily Snowfall Forecast';
    const optPeriod = widget.options?.period || 'auto';
    const requestedHorizon = optPeriod !== 'auto' ? optPeriod : ctx.viewDays;
    const days = aggregateDailySnow(ctx, requestedHorizon);

    const lat = ctx.lat;
    const lon = ctx.lon;
    const isCA = lat >= 41.6 && lat <= 83.1 && lon >= -141.0 && lon <= -52.6 && (lat >= 49.0 || (lat >= 42.0 && lon < -79.0));
    const isUS = (lat >= 24.5 && lat <= 49.38 && lon >= -124.8 && lon <= -66.9) ||
                 (lat >= 51.0 && lat <= 71.5 && lon >= -179.0 && lon <= -129.0) ||
                 (lat >= 18.9 && lat <= 22.3 && lon >= -160.3 && lon <= -154.8);
    const autoAgency = isUS ? 'NWS' : (isCA ? 'ECCC' : 'ECMWF');

    const activeSourceKey = (widget.options?.dataSource && widget.options.dataSource !== 'auto')
      ? widget.options.dataSource
      : (ctx.activeTabId && ctx.activeTabId !== 'blend' ? ctx.activeTabId : 'auto');

    const sourceBadge = resolveSourceBadge(ctx, activeSourceKey);
    const isImperial = store.getState().units === 'imperial';

    const activeH = String(requestedHorizon).toLowerCase();
    const is3 = activeH.startsWith('3');
    const is5 = activeH.startsWith('5');
    const is7 = activeH.startsWith('7');
    const isMax = activeH === 'max' || activeH === '10';

    // Summary statistics
    const totalSnowIn = days.reduce((sum, d) => sum + d.snowIn, 0);
    const maxDay = days.reduce((max, d) => (d.snowIn > max.snowIn ? d : max), days[0] || { dayLabel: '--', snowIn: 0 });
    const snowDays = days.filter(d => d.snowIn >= 0.1).length;

    const totalDisplay = isImperial
      ? `${totalSnowIn.toFixed(1)}″`
      : `${(Math.round(totalSnowIn * 2.54 * 10) / 10).toFixed(1)} cm`;

    const maxDayDisplay = isImperial
      ? `${maxDay?.snowIn.toFixed(1)}″`
      : `${(Math.round((maxDay?.snowIn || 0) * 2.54 * 10) / 10).toFixed(1)} cm`;

    const maxSnowVal = days.length ? Math.max(1.0, ...days.map(d => d.snowIn)) : 5.0;

    const showSummaryBanner = widget.options?.showSummaryBanner !== false;

    const rowsHtml = days.map(d => {
      const barWidthPct = d.snowIn > 0
        ? Math.min(100, Math.max(5, Math.round((d.snowIn / maxSnowVal) * 100)))
        : 0;

      const snowProbHtml = d.snowProbPct > 0
        ? `<span style="font-size: 0.72rem; font-weight: 700; color: #a5b4fc; font-family: monospace; display: inline-flex; align-items: center; gap: 2px;">❄️${d.snowProbPct}%</span>`
        : `<span style="font-size: 0.72rem; color: #64748b; opacity: 0.4;">--</span>`;

      return `
        <div style="display: grid; grid-template-columns: 80px 28px 46px 1fr 64px; align-items: center; gap: 8px; padding: 7px 0; border-bottom: 1px solid rgba(255, 255, 255, 0.05); font-size: 0.8rem;">
          <div style="font-weight: 700; color: #f8fafc; line-height: 1.2;">
            <div>${d.dayLabel}</div>
            <div style="font-size: 0.65rem; color: #94a3b8; font-weight: 500;">${d.dateSub}</div>
          </div>
          <div style="font-size: 1.2rem; text-align: center; line-height: 1;" title="${d.condition}">${d.icon}</div>
          <div>${snowProbHtml}</div>
          <div style="display: flex; align-items: center; gap: 8px; min-width: 0;">
            <div style="flex: 1; height: 7px; background: rgba(255, 255, 255, 0.07); border-radius: 999px; position: relative; overflow: hidden;">
              ${barWidthPct > 0 ? `
              <div style="position: absolute; top: 0; bottom: 0; left: 0; width: ${barWidthPct}%; border-radius: 999px; background: linear-gradient(90deg, #6366f1 0%, #38bdf8 60%, #ffffff 100%); box-shadow: 0 0 8px rgba(255, 255, 255, 0.45);"></div>
              ` : ''}
            </div>
          </div>
          <div style="font-size: 0.8rem; font-weight: 700; color: ${d.snowIn > 0 ? '#ffffff' : '#94a3b8'}; text-align: right; font-family: monospace;">
            ${d.snowDisplay}
          </div>
        </div>
      `;
    }).join('');

    return `
      <div class="metric-card" style="background: linear-gradient(145deg, #131226 0%, #0d0e19 100%); border: 1px solid rgba(129, 140, 248, 0.25); border-radius: 14px; padding: 14px 16px; box-shadow: 0 8px 24px rgba(0,0,0,0.35);">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; flex-wrap: wrap; gap: 8px;">
          <div style="display: flex; align-items: center; gap: 8px; flex-wrap: wrap;">
            <span style="font-size: 0.95rem; font-weight: 700; color: #fff;">❄️ ${title}</span>
            ${widget.options?.allowSourceSelection !== false ? `
            <div style="display: inline-flex; align-items: center; gap: 4px;">
              <span style="font-size: 0.65rem; color: #94a3b8; font-weight: 600;">SOURCE:</span>
              <select class="daily-source-select" style="background: rgba(129, 140, 248, 0.15); color: #c7d2fe; border: 1px solid rgba(129, 140, 248, 0.4); border-radius: 6px; padding: 2px 6px; font-size: 0.7rem; font-weight: 700; cursor: pointer; outline: none;">
                <option value="auto" ${activeSourceKey === 'auto' ? 'selected' : ''}>Auto (${autoAgency})</option>
                <option value="nws" ${activeSourceKey === 'nws' ? 'selected' : ''}>NWS (NOAA)</option>
                <option value="eccc" ${activeSourceKey === 'eccc' ? 'selected' : ''}>ECCC (Canada)</option>
                <option value="ecmwf" ${activeSourceKey === 'ecmwf' ? 'selected' : ''}>ECMWF (IFS)</option>
                <option value="icon13" ${activeSourceKey === 'icon13' ? 'selected' : ''}>ICON-13 (DWD)</option>
                <option value="hrrr" ${activeSourceKey === 'hrrr' ? 'selected' : ''}>HRRR (3km)</option>
                <option value="rrfs" ${activeSourceKey === 'rrfs' ? 'selected' : ''}>RRFS (3km)</option>
                <option value="rdps" ${activeSourceKey === 'rdps' ? 'selected' : ''}>RDPS (10km)</option>
                <option value="gdps" ${activeSourceKey === 'gdps' ? 'selected' : ''}>GDPS (15km)</option>
                <option value="gfs" ${activeSourceKey === 'gfs' ? 'selected' : ''}>GFS (0.25°)</option>
              </select>
            </div>
            ` : `
            <span style="font-size: 0.65rem; padding: 2px 6px; background: rgba(129, 140, 248, 0.15); color: #c7d2fe; border-radius: 4px; font-weight: 700; border: 1px solid rgba(129, 140, 248, 0.3); letter-spacing: 0.3px;">${sourceBadge}</span>
            `}
          </div>
          <div class="daily-horizon-pills" style="display: inline-flex; background: rgba(0, 0, 0, 0.45); border-radius: 6px; padding: 2px; border: 1px solid rgba(255, 255, 255, 0.08);">
            <button class="daily-h-btn ${is3 ? 'active' : ''}" data-days="3" style="background: ${is3 ? '#818cf8' : 'transparent'}; color: ${is3 ? '#090a0f' : '#94a3b8'}; border: none; font-size: 0.7rem; font-weight: 700; padding: 2px 7px; border-radius: 4px; cursor: pointer;">3D</button>
            <button class="daily-h-btn ${is5 ? 'active' : ''}" data-days="5" style="background: ${is5 ? '#818cf8' : 'transparent'}; color: ${is5 ? '#090a0f' : '#94a3b8'}; border: none; font-size: 0.7rem; font-weight: 700; padding: 2px 7px; border-radius: 4px; cursor: pointer;">5D</button>
            <button class="daily-h-btn ${is7 ? 'active' : ''}" data-days="7" style="background: ${is7 ? '#818cf8' : 'transparent'}; color: ${is7 ? '#090a0f' : '#94a3b8'}; border: none; font-size: 0.7rem; font-weight: 700; padding: 2px 7px; border-radius: 4px; cursor: pointer;">7D</button>
            <button class="daily-h-btn ${isMax ? 'active' : ''}" data-days="max" style="background: ${isMax ? '#818cf8' : 'transparent'}; color: ${isMax ? '#090a0f' : '#94a3b8'}; border: none; font-size: 0.7rem; font-weight: 700; padding: 2px 7px; border-radius: 4px; cursor: pointer;">Max</button>
          </div>
        </div>

        ${showSummaryBanner && days.length > 0 ? `
        <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; background: rgba(99, 102, 241, 0.08); border: 1px solid rgba(129, 140, 248, 0.2); border-radius: 8px; padding: 8px 10px; margin-bottom: 12px; text-align: center;">
          <div>
            <div style="font-size: 0.65rem; color: #94a3b8; font-weight: 600; text-transform: uppercase;">Storm Total</div>
            <div style="font-size: 1.05rem; font-weight: 800; color: #ffffff; font-family: monospace; text-shadow: 0 0 8px rgba(255, 255, 255, 0.4);">${totalDisplay}</div>
          </div>
          <div>
            <div style="font-size: 0.65rem; color: #94a3b8; font-weight: 600; text-transform: uppercase;">Peak Day</div>
            <div style="font-size: 0.95rem; font-weight: 700; color: #f8fafc; font-family: monospace;">${maxDay?.dayLabel || '--'} <span style="font-size: 0.75rem; color: #a5b4fc;">(${maxDayDisplay})</span></div>
          </div>
          <div>
            <div style="font-size: 0.65rem; color: #94a3b8; font-weight: 600; text-transform: uppercase;">Snow Days</div>
            <div style="font-size: 1.05rem; font-weight: 800; color: #f8fafc; font-family: monospace;">${snowDays} <span style="font-size: 0.7rem; color: #94a3b8; font-weight: 500;">/ ${days.length}d</span></div>
          </div>
        </div>
        ` : ''}

        <div>
          ${rowsHtml || ((ctx.data?.accessDenial || ForecastBinaryService.lastAccessDenial || !TierAccessService.userHasLayerAccess('forecast_point_tiles'))
            ? TierAccessService.buildTrendChartDenialHtml({
                denial: ctx.data?.accessDenial || ForecastBinaryService.lastAccessDenial,
                requiredTier: 'pro',
                featureLabel: `${title} (${activeSourceKey.toUpperCase()})`
              })
            : '<div style="color: #94a3b8; font-size: 0.8rem; text-align: center; padding: 12px;">No snowfall forecast data available</div>')}
        </div>
      </div>
    `;
  }

  public init(widget: ForecastWidgetConfig, ctx: WidgetRenderContext): void {
    const container = document.querySelector(`[data-widget-id="${widget.id}"]`) || document;
    const buttons = container.querySelectorAll('.daily-h-btn');
    buttons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const d = (btn as HTMLElement).dataset.days;
        if (d && ctx.onHorizonChange) {
          ctx.onHorizonChange(d === 'max' ? 'max' : parseInt(d, 10));
        }
      });
    });

    const sourceSelect = container.querySelector('.daily-source-select') as HTMLSelectElement;
    if (sourceSelect) {
      sourceSelect.addEventListener('change', (e) => {
        e.stopPropagation();
        const val = sourceSelect.value;
        if (ctx.onSourceChange) {
          ctx.onSourceChange(val);
        }
      });
    }
  }
}
